import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FileText, Award, Download, GraduationCap, ClipboardCheck, Loader2, CheckCircle2,
  XCircle, Upload, Sparkles, AlertCircle, FileSignature
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../ui/dialog';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '';

const download = async (url, filename) => {
  try {
    const res = await axios.get(`${BACKEND}${url}`, { withCredentials: true, responseType: 'blob' });
    const objUrl = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = objUrl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(objUrl);
  } catch (e) {
    toast.error('Não foi possível baixar o documento');
  }
};

const ScoreBadge = ({ score, passed }) => (
  <span className={`px-2.5 py-1 rounded-full text-sm font-bold ${passed ? 'bg-green-900/50 text-green-300 border border-green-700/60' : 'bg-red-900/40 text-red-300 border border-red-700/60'}`}>
    {score?.toFixed(1)} {passed ? '✓' : ''}
  </span>
);

// ---------------- Documentos ----------------
const DocumentsTab = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/student/documents`, { withCredentials: true })
      .then(r => setDocs(r.data.documents))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-red-500" size={28} /></div>;
  if (docs.length === 0) return <div className="text-center text-gray-400 py-16" data-testid="documents-empty">Nenhum documento disponível ainda. Seus contratos e certificados aparecerão aqui.</div>;

  const iconFor = (t) => t === 'certificado' ? <Award className="text-amber-400" size={22} /> : <FileSignature className="text-red-400" size={22} />;

  return (
    <div className="space-y-3" data-testid="documents-list">
      {docs.map((d, i) => (
        <div key={d.id + i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center justify-between gap-4" data-testid={`document-${d.type}-${i}`}>
          <div className="flex items-center gap-3 min-w-0">
            {iconFor(d.type)}
            <div className="min-w-0">
              <div className="text-white font-medium truncate">{d.title}</div>
              <div className="text-gray-500 text-xs">{d.reference} · {fmtDate(d.date)}</div>
            </div>
          </div>
          {d.download_url ? (
            <Button size="sm" variant="outline" onClick={() => download(d.download_url, `${d.type}-${d.reference || d.id}.pdf`)} data-testid={`download-${i}`}>
              <Download size={15} className="mr-1.5" />Baixar
            </Button>
          ) : (
            <Badge className="bg-zinc-700 text-gray-300 shrink-0">Aguardando assinatura</Badge>
          )}
        </div>
      ))}
    </div>
  );
};

// ---------------- Revisão da prova (IA) ----------------
const ExamReviewModal = ({ examId, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && examId) {
      setLoading(true); setData(null);
      axios.get(`${API}/student/exam/${examId}/review`, { withCredentials: true })
        .then(r => setData(r.data))
        .catch(() => toast.error('Erro ao carregar revisão'))
        .finally(() => setLoading(false));
    }
  }, [open, examId]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="exam-review-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="text-fuchsia-400" size={18} />Revisão da Prova</DialogTitle>
          <DialogDescription className="text-gray-400">Veja onde você errou e entenda o porquê (explicação gerada por IA).</DialogDescription>
        </DialogHeader>
        {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-red-500" /></div> : data && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <ScoreBadge score={data.score} passed={data.passed} />
              <span className="text-gray-400">{data.wrong_count} erro(s) em {data.total} questões</span>
            </div>
            {data.wrong_count === 0 ? (
              <p className="text-green-300 text-sm">Parabéns! Você acertou todas as questões. 🎉</p>
            ) : data.wrong_questions.map((w) => (
              <div key={w.question_id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4" data-testid={`review-wrong-${w.question_id}`}>
                <p className="text-white font-medium mb-2">{w.question}</p>
                <div className="text-sm space-y-1">
                  <p className="text-red-300 flex items-center gap-1.5"><XCircle size={14} />Sua resposta: {w.user_answer?.toUpperCase() || '—'}</p>
                  <p className="text-green-300 flex items-center gap-1.5"><CheckCircle2 size={14} />Correta: {w.correct_answer?.toUpperCase()}</p>
                </div>
                {w.explanation && (
                  <div className="mt-3 bg-fuchsia-950/30 border border-fuchsia-800/40 rounded-md p-3 text-sm text-fuchsia-100">
                    <span className="font-semibold text-fuchsia-300">Explicação: </span>{w.explanation}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Resultado da entrega ----------------
const DeliverableResult = ({ result }) => (
  <div className="space-y-3" data-testid="deliverable-result">
    <div className="flex items-center gap-3">
      <ScoreBadge score={result.score} passed={result.passed} />
      <span className={result.passed ? 'text-green-300' : 'text-red-300'}>{result.passed ? 'Aprovado!' : 'Ainda não atingiu 8,0'}</span>
    </div>
    {result.feedback && <p className="text-gray-300 text-sm">{result.feedback}</p>}
    {result.strengths?.length > 0 && (
      <div>
        <p className="text-green-400 text-sm font-semibold mb-1">Pontos fortes</p>
        <ul className="list-disc list-inside text-gray-300 text-sm space-y-0.5">{result.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
      </div>
    )}
    {result.improvements?.length > 0 && (
      <div>
        <p className="text-amber-400 text-sm font-semibold mb-1">O que melhorar</p>
        <ul className="list-disc list-inside text-gray-300 text-sm space-y-0.5">{result.improvements.map((s, i) => <li key={i}>{s}</li>)}</ul>
      </div>
    )}
    {result.certificate_id && (
      <div className="bg-green-900/20 border border-green-700/50 rounded-md p-3 text-sm text-green-200 flex items-center gap-2">
        <Award size={16} />Certificado liberado! Confira a aba Documentos.
      </div>
    )}
  </div>
);

// ---------------- Envio de entrega (IA) ----------------
const DeliverableModal = ({ assessment, open, onClose, onGraded }) => {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [attachment, setAttachment] = useState(null); // {attachment_url, attachment_name, extracted_text}
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (open) { setText(''); setResult(null); setAttachment(null); } }, [open]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error('Arquivo excede 25 MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await axios.post(`${API}/student/deliverables/upload`, fd, { withCredentials: true });
      setAttachment(r.data);
      toast.success('Arquivo anexado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha no upload');
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (text.trim().length < 20 && !attachment) { toast.error('Escreva sua entrega ou anexe um arquivo'); return; }
    setSubmitting(true);
    try {
      let fullText = text;
      if (attachment?.extracted_text) fullText += `\n\n[Conteúdo do arquivo anexado "${attachment.attachment_name}"]:\n${attachment.extracted_text}`;
      else if (attachment) fullText += `\n\n[O aluno anexou o arquivo "${attachment.attachment_name}".]`;
      const r = await axios.post(`${API}/student/deliverables/${assessment.assessment_id}/submit`, {
        text: fullText,
        attachment_url: attachment?.attachment_url || '',
        attachment_name: attachment?.attachment_name || '',
      }, { withCredentials: true });
      setResult(r.data);
      onGraded && onGraded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar a entrega');
    } finally { setSubmitting(false); }
  };

  if (!assessment) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="deliverable-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="text-red-400" size={18} />{assessment.title}</DialogTitle>
          <DialogDescription className="text-gray-400">{assessment.description}</DialogDescription>
        </DialogHeader>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 mb-2">
          <p className="text-gray-300 text-sm font-semibold mb-1">Critérios de avaliação (nota mínima {assessment.pass_score})</p>
          <ul className="list-disc list-inside text-gray-400 text-sm space-y-0.5">{(assessment.criteria || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
        {result ? <DeliverableResult result={result} /> : (
          <>
            <Textarea value={text} onChange={e => setText(e.target.value)} rows={8}
              placeholder="Escreva aqui sua entrega / resolução..." className="bg-zinc-900 border-zinc-700 text-white" data-testid="deliverable-textarea" />
            <div className="mt-2">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer bg-zinc-900 border border-zinc-700 border-dashed rounded-lg px-3 py-2.5 hover:border-red-500/50 transition-colors w-fit" data-testid="deliverable-file-label">
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                {attachment ? `Anexado: ${attachment.attachment_name}` : 'Anexar arquivo (PDF, imagem, RVT, DWG...)'}
                <input type="file" className="hidden" onChange={onFile} accept=".pdf,.png,.jpg,.jpeg,.txt,.rvt,.rfa,.ifc,.dwg,.zip,.docx" data-testid="deliverable-file-input" />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={onClose}>Fechar</Button>
              <Button onClick={submit} disabled={submitting || uploading} className="bg-red-600 hover:bg-red-700" data-testid="deliverable-submit">
                {submitting ? <><Loader2 className="animate-spin mr-2" size={16} />Corrigindo com IA...</> : <><Upload size={16} className="mr-2" />Enviar para correção</>}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Submission review (feedback já corrigido) ----------------
const SubmissionReviewModal = ({ submissionId, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (open && submissionId) {
      setLoading(true); setData(null);
      axios.get(`${API}/student/deliverables/submission/${submissionId}`, { withCredentials: true })
        .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    }
  }, [open, submissionId]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="submission-review-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="text-fuchsia-400" size={18} />{data?.assessment_title || 'Entrega'}</DialogTitle></DialogHeader>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-red-500" /></div> : data && <DeliverableResult result={data} />}
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Notas & Avaliações ----------------
const GradesTab = () => {
  const [grades, setGrades] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [examReview, setExamReview] = useState(null);
  const [subReview, setSubReview] = useState(null);
  const [activeDeliverable, setActiveDeliverable] = useState(null);

  const load = () => {
    Promise.all([
      axios.get(`${API}/student/grades`, { withCredentials: true }),
      axios.get(`${API}/student/deliverables`, { withCredentials: true }),
    ]).then(([g, d]) => { setGrades(g.data.courses); setDeliverables(d.data.deliverables); })
      .catch(() => {}).finally(() => setFirstLoad(false));
  };
  useEffect(() => { load(); }, []);

  if (firstLoad) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-red-500" size={28} /></div>;

  return (
    <div className="space-y-6" data-testid="grades-tab">
      {/* Avaliações de entrega disponíveis */}
      {deliverables.length > 0 && (
        <div>
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><ClipboardCheck className="text-red-400" size={18} />Avaliações de Entrega</h3>
          <div className="space-y-3">
            {deliverables.map(a => (
              <div key={a.assessment_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`deliverable-${a.assessment_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-white font-medium">{a.title}</div>
                    <div className="text-gray-500 text-xs">{a.course_title} · nota mínima {a.pass_score}</div>
                  </div>
                  {a.last_submission && <ScoreBadge score={a.last_submission.score} passed={a.last_submission.passed} />}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={() => setActiveDeliverable(a)} className="bg-red-600 hover:bg-red-700" data-testid={`open-deliverable-${a.assessment_id}`}>
                    {a.last_submission ? 'Reenviar' : 'Fazer entrega'}
                  </Button>
                  {a.last_submission && (
                    <Button size="sm" variant="outline" onClick={() => setSubReview(a.last_submission.submission_id)}>Ver feedback</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notas por curso */}
      <div>
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><GraduationCap className="text-amber-400" size={18} />Minhas Notas</h3>
        {grades.length === 0 ? (
          <div className="text-center text-gray-400 py-10" data-testid="grades-empty">Você ainda não realizou avaliações.</div>
        ) : grades.map(course => (
          <div key={course.course_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-3">
            <div className="text-white font-medium mb-2">{course.course_title}</div>
            <div className="space-y-2">
              {course.items.map(item => (
                <div key={item.id} className="flex items-center justify-between border-t border-zinc-800 pt-2 first:border-0 first:pt-0" data-testid={`grade-item-${item.id}`}>
                  <div className="text-sm">
                    <span className="text-gray-300">{item.title}</span>
                    <span className="text-gray-500 text-xs ml-2">{fmtDate(item.date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ScoreBadge score={item.score} passed={item.passed} />
                    {item.kind === 'prova'
                      ? <Button size="sm" variant="outline" onClick={() => setExamReview(item.id)} data-testid={`review-exam-${item.id}`}>Revisão</Button>
                      : <Button size="sm" variant="outline" onClick={() => setSubReview(item.id)}>Feedback</Button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <ExamReviewModal examId={examReview} open={!!examReview} onClose={() => setExamReview(null)} />
      <SubmissionReviewModal submissionId={subReview} open={!!subReview} onClose={() => setSubReview(null)} />
      <DeliverableModal assessment={activeDeliverable} open={!!activeDeliverable} onClose={() => setActiveDeliverable(null)} onGraded={load} />
    </div>
  );
};

const StudentCenter = ({ tab }) => {
  if (tab === 'documentos') return <DocumentsTab />;
  if (tab === 'notas') return <GradesTab />;
  return null;
};

export default StudentCenter;
